import { BannerDiscountCode } from '@src/components/banner-discount-code/BannerDiscountCode.component'
import { Disclaimer, DisclaimerProps } from '@src/components/disclaimer/Disclaimer.component'
import { EffectivenessProps } from '@src/components/effectiveness/Effectiveness.component'
import { EffectivenessSection } from '@src/components/effectiveness-section/EffectivenessSection.component'
import { FaqsSection, FaqsSectionProps } from '@src/components/faqs-section/FaqsSection.component'
import {
  HardwareHowTo,
  HardwareHowToProps,
} from '@src/components/harware-how-to/HardwareHowTo.component'
import { HeaderUncoupled } from '@src/components/header-uncoupled/HeaderUncoupled.component'
import { HowNcWorks, HowNcWorksProps } from '@src/components/how-nc-works/HowNcWorks.component'
import {
  InfluencerHero,
  InfluencerHeroProps,
} from '@src/components/influencer-hero/InfluencerHero.component'
import { LastChance, LastChanceProps } from '@src/components/last-chance/LastChance.component'
import { Page } from '@src/components/page/Page.component'
import { Seo, SeoMetaTag, SeoProps } from '@src/components/seo/Seo.component'
import { UncoupledFooter } from '@src/components/uncoupled-footer/UncoupledFooter.component'
import {
  WebOnboardingIntro,
  WebOnboardingIntroProps,
} from '@src/components/web-onboarding-intro/WebOnboardingIntro.component'
import { MigratedPageProps } from '@src/helpers/gatsby/pages'
import { useAppSelector } from '@src/hooks/useAppSelector'
import { useImpression } from '@src/hooks/useImpression'
import { mixpanelService } from '@src/srv/mixpanel.service'
import { GeolocationCountry } from '@src/store/geolocation/geolocation.slice'
import { DatoCmsFileField } from 'dato-cms-assets'
import { ReactNode } from 'react'
interface InfluencerPageProps
  extends MigratedPageProps<{
    page: {
      _seoMetaTags: SeoMetaTag[]
      seo: Omit<SeoProps, '_seoMetaTags'>
      influencerHero: InfluencerHeroProps
      howDoesNcWork: [HowNcWorksProps]
      signupHeaderText: string
      heading: string
      subheading: string
      signupButtonText: string
      profileImages: DatoCmsFileField[]
      hardwareUsage: HardwareHowToProps
      effectivenessComparison: EffectivenessProps
      webonboardingIntro: [WebOnboardingIntroProps]
      faqs: Omit<FaqsSectionProps, 'questions'> & { questionAnswers: FaqsSectionProps['questions'] }
      lastChance: LastChanceProps
      lastChanceAlternative: LastChanceProps
      disclaimer: DisclaimerProps
    }
    campaign: InfluencerHeroProps['page']
  }> {}

function InfluencerPage({ pageContext }: InfluencerPageProps): ReactNode {
  const {
    _seoMetaTags,
    seo,
    hardwareUsage,
    howDoesNcWork,
    signupHeaderText,
    heading,
    subheading,
    signupButtonText,
    profileImages,
    effectivenessComparison,
    webonboardingIntro,
    faqs,
    lastChance,
    lastChanceAlternative,
    disclaimer,
  } = pageContext.page

  const windowLoaded = typeof window !== 'undefined'
  const savedName = windowLoaded && window.sessionStorage.getItem('discountName')
  const savedCode = windowLoaded && window.sessionStorage.getItem('discountCode')

  const reduxName = useAppSelector(state => state.discountCode.name)
  const reduxCode = useAppSelector(state => state.discountCode.code)
  const country = useAppSelector(state => state.geolocation.country)
  const lastChanceProps = country === GeolocationCountry.US ? lastChance : lastChanceAlternative

  const name = reduxName || savedName
  const code = reduxCode || savedCode

  useImpression(() => {
    mixpanelService.track('InfluencerPageImpression', {
      name,
      code,
      campaign: pageContext.campaign,
    })
  })

  return (
    <Page>
      <Seo noIndex {...seo} _seoMetaTags={_seoMetaTags} />
      <HeaderUncoupled signupButtonText={signupHeaderText} theme="shimmerGradient" />
      <BannerDiscountCode page="influencer" />
      <InfluencerHero
        page={pageContext.campaign}
        heading={heading}
        subheading={subheading}
        signupText={signupButtonText}
        profileImages={profileImages}
      />

      <HowNcWorks {...howDoesNcWork[0]} />
      <HardwareHowTo {...hardwareUsage} headingSize="title-l" />
      <EffectivenessSection {...effectivenessComparison} />
      <WebOnboardingIntro
        heading={webonboardingIntro[0].heading}
        cta={[
          {
            text: webonboardingIntro[0].cta[0]!.text,
            url: webonboardingIntro[0].cta[0]!.url,
          },
        ]}
      />
      <FaqsSection heading={faqs.heading} questions={faqs.questionAnswers} theme="white" />
      <LastChance
        theme="black"
        signupButtonTheme="brandPurple"
        contentPlacement="left"
        {...lastChanceProps}
      />
      <Disclaimer {...disclaimer} />
      <UncoupledFooter />
    </Page>
  )
}

export default InfluencerPage
